import { useEffect, useState } from "react";
import { api, type Account } from "./api";
import { Link } from "./Link";
import { SpamCheck } from "./SpamCheck";
import { Field, Notice } from "./ui";

export function SignIn({
  next,
  account,
  go,
  turnstileSiteKey,
}: {
  next: string;
  account: Account | null;
  go: (href: string) => void;
  turnstileSiteKey: string | null;
}) {
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");
  const [spamToken, setSpamToken] = useState("");
  const [spamFailed, setSpamFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  if (account)
    return (
      <main id="main" className="app-main sign-in">
        <section className="stack">
          <h1>You're signed in.</h1>
          <p>Signed in as {account.email}.</p>
          <Link go={go} className="primary" href={next}>
            Continue
          </Link>
        </section>
      </main>
    );
  return (
    <main id="main" className="app-main sign-in">
      {sentTo ? (
        <section className="stack" aria-live="polite">
          <h1>Check your email.</h1>
          <p>
            We sent a sign-in link to <strong>{sentTo}</strong>. It works once
            and expires in 15 minutes. You can close this tab.
          </p>
          <button
            type="button"
            className="text-button"
            onClick={() => {
              setSentTo("");
              setEmail("");
            }}
          >
            Use a different email
          </button>
        </section>
      ) : (
        <section className="stack">
          <h1>Sign in to Spike Easy.</h1>
          <p>
            See your ranking and score your own matches. If you're new, signing
            in creates your profile.
          </p>
          <form
            className="stack"
            onSubmit={async (e) => {
              e.preventDefault();
              if (turnstileSiteKey && !spamToken) {
                setProblem(
                  spamFailed
                    ? "The spam check couldn't load. Reload the page and try again."
                    : "The spam check is still running. Try again in a moment.",
                );
                return;
              }
              setBusy(true);
              setProblem("");
              try {
                await api("/auth/email", {
                  email,
                  next,
                  ...(turnstileSiteKey ? { turnstile: spamToken } : {}),
                });
                setSentTo(email.trim().toLowerCase());
              } catch (e) {
                setProblem((e as Error).message);
              } finally {
                setBusy(false);
                // Each Turnstile token works once, so the next request needs a fresh widget.
                setSpamToken("");
                setAttempt((n) => n + 1);
              }
            }}
          >
            <Field label="Email address">
              <input
                type="email"
                autoComplete="email"
                inputMode="email"
                required
                maxLength={254}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            {turnstileSiteKey && (
              <SpamCheck
                key={attempt}
                siteKey={turnstileSiteKey}
                action="sign_in"
                onToken={(token) => {
                  setSpamToken(token);
                  if (token) setSpamFailed(false);
                }}
                onError={() => setSpamFailed(true)}
              />
            )}
            {problem && <Notice error>{problem}</Notice>}
            <button className="primary" disabled={busy}>
              {busy ? "Sending…" : "Email me a sign-in link"}
            </button>
          </form>
          <p className="muted">
            Scoring for a team without an account? Open your tournament and
            choose “use your team phrase”. We use your email only to sign you
            in and to link your results; see the{" "}
            <a href="https://spike-easy.com/privacy">privacy policy</a>.
          </p>
        </section>
      )}
    </main>
  );
}

export function EmailSignIn({
  go,
  signedIn,
}: {
  go: (href: string) => void;
  signedIn: (next: string) => Promise<void>;
}) {
  const [token] = useState(
    () => new URLSearchParams(location.hash.slice(1)).get("token") ?? "",
  );
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(
    token ? "" : "This sign-in link is incomplete. Request a new one.",
  );
  const [email, setEmail] = useState("");
  useEffect(() => {
    // The token must not stay in the address bar or browser history.
    if (location.hash) history.replaceState({}, "", location.pathname);
    if (!token) return;
    let current = true;
    api<{ email: string }>("/auth/email/peek", { token })
      .then((result) => current && setEmail(result.email))
      .catch((e: Error) => current && setProblem(e.message));
    return () => {
      current = false;
    };
  }, [token]);
  return (
    <main id="main" className="app-main sign-in">
      <section className="stack">
        <h1>Finish signing in.</h1>
        {problem ? (
          <>
            <Notice error>{problem}</Notice>
            <Link go={go} className="primary" href="/sign-in">
              Request a new link
            </Link>
          </>
        ) : !email ? (
          <Notice>Checking your link…</Notice>
        ) : (
          <>
            <p>
              This link signs you in as <strong>{email}</strong> on this device.
              If that isn't your email address, close this page: someone may
              have sent you a link to their own account.
            </p>
            <button
              className="primary"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const { next } = await api<{ next: string }>(
                    "/auth/email/verify",
                    { token },
                  );
                  await signedIn(next);
                } catch (e) {
                  setProblem((e as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </>
        )}
      </section>
    </main>
  );
}
